import { type KeyboardEvent, type ReactNode, useRef, useState } from "react";

import { useOutsideDismiss } from "@/hooks/use-outside-dismiss";
import { cn } from "@/lib/utils";

/** One day's column of a chart, as its target: what it says in words, and its tooltip. */
export interface DayColumnTarget {
  /** The day, "2026-10-15": the column's key. */
  day: string;
  /** The day's figures in words, the column's name for screen readers: "do 15/10/2026: 46 oproepen, 1 fout". */
  label: string;
  /** What the tooltip shows: the same figures, laid out for the eye. */
  tooltip: ReactNode;
}

/** What the arrow keys and Home and End move the focus to, from a column. */
const STEPS: Record<string, (index: number, count: number) => number> = {
  ArrowLeft: (index) => Math.max(0, index - 1),
  ArrowRight: (index, count) => Math.min(count - 1, index + 1),
  Home: () => 0,
  End: (_, count) => count - 1,
};

/** Where a column's tooltip opens: from its left edge near the chart's left, from its right edge near the right. */
function tooltipSide(index: number, count: number): string {
  if (index < count / 3) {
    return "left-0";
  }
  return index >= (count * 2) / 3 ? "right-0" : "left-1/2 -translate-x-1/2";
}

interface DayColumnTargetsProps {
  /** Every day of the chart, in order, one band each. */
  columns: readonly DayColumnTarget[];
  /** Places the row over the plot area, such as `absolute inset-x-0 top-5 bottom-0`. */
  className?: string;
}

/**
 * The days of a column chart as targets laid over its plot, one band per day,
 * so every day's figures reach everyone: a mouse shows a day's tooltip on
 * hover, a tap shows it on a touch screen (where nothing hovers), and the
 * keyboard reaches the chart with one Tab and moves between days with the
 * arrows (Home and End for the first and the last), the day in focus ringed
 * and its tooltip shown above the plot, clear of the columns' value labels
 * (in the 20 px band over the plot). A screen reader reads each day's figures
 * (`label`). Escape, a press outside the chart or the focus leaving it
 * closes the tooltip. Used by `<CallsChart>`; draw the columns themselves
 * underneath, `aria-hidden`:
 *
 *     <div className="relative h-44 pt-5">
 *       <svg aria-hidden="true" …>…</svg>
 *       <DayColumnTargets columns={targets} className="absolute inset-x-0 top-5 bottom-0" />
 *     </div>
 */
export function DayColumnTargets({ columns, className }: DayColumnTargetsProps) {
  // The day whose tooltip shows, and the day the Tab key lands on.
  const [active, setActive] = useState<string | null>(null);
  const [focusDay, setFocusDay] = useState<string | null>(null);
  const row = useRef<HTMLDivElement>(null);
  const dismiss = useOutsideDismiss(active !== null, () => setActive(null));
  const tabDay = columns.some((column) => column.day === focusDay)
    ? focusDay
    : (columns.at(-1)?.day ?? null);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const step = STEPS[event.key];
    if (!step) {
      return;
    }
    event.preventDefault();
    const next = columns[step(index, columns.length)];
    if (next) {
      setFocusDay(next.day);
      row.current?.querySelector<HTMLButtonElement>(`[data-day="${next.day}"]`)?.focus();
    }
  };

  return (
    <div ref={row} className={cn("flex", className)} {...dismiss}>
      {columns.map((column, index) => (
        <div key={column.day} className="relative flex-1">
          <button
            type="button"
            data-day={column.day}
            tabIndex={column.day === tabDay ? 0 : -1}
            aria-label={column.label}
            onFocus={() => {
              setFocusDay(column.day);
              setActive(column.day);
            }}
            onBlur={() => setActive((day) => (day === column.day ? null : day))}
            onClick={() => setActive(column.day)}
            onPointerEnter={(event) => event.pointerType === "mouse" && setActive(column.day)}
            onPointerLeave={(event) =>
              event.pointerType === "mouse" && setActive((day) => (day === column.day ? null : day))
            }
            onKeyDown={(event) => onKeyDown(event, index)}
            className="focus-visible:ring-ring absolute inset-0 rounded-sm focus-visible:ring-2 focus-visible:outline-none"
          />
          {active === column.day && (
            <span
              aria-hidden="true"
              className={cn(
                // Above the plot and its value labels, so it never covers the day's own figure.
                "bg-foreground text-background pointer-events-none absolute bottom-full z-10 mb-6 rounded-sm px-1.5 py-1 text-xs whitespace-nowrap",
                tooltipSide(index, columns.length),
              )}
            >
              {column.tooltip}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}
